import React, { useState, useRef, useCallback } from "react";
import { Box, Container, useTheme } from "@mui/material";
import { FeaturedCarousel } from "../Dashboard/FeaturedCarousel";
import { MobileDashboardTabs } from "./MobileDashboardTabs";
import { MobileOverviewContent } from "./MobileOverviewContent";
import { MobileUpsellContent } from "./MobileUpsellContent";

export const MobileAppDashboard: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [activeTab, setActiveTab] = useState<number>(0);

  // Touch gesture support for swiping between tabs
  const touchStartCoords = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartCoords.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartCoords.current) return;

      const deltaX = e.changedTouches[0].clientX - touchStartCoords.current.x;
      const deltaY = e.changedTouches[0].clientY - touchStartCoords.current.y;
      touchStartCoords.current = null;

      // Threshold: horizontal swipe must be at least 60px and more horizontal than vertical
      const isHorizontalSwipe =
        Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3;

      if (isHorizontalSwipe) {
        if (deltaX < 0 && activeTab === 0) {
          // Swipe left -> Next tab (Upsell)
          setActiveTab(1);
        } else if (deltaX > 0 && activeTab === 1) {
          // Swipe right -> Previous tab (Overview)
          setActiveTab(0);
        }
      }
    },
    [activeTab],
  );

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: isDark ? "#0f172a" : "#f8fafc",
        color: theme.palette.text.primary,
        overflowX: "hidden",
        position: "relative",
        // Padding bottom ensures content is never hidden behind the floating bottom bar
        pb: "calc(20px + env(safe-area-inset-bottom, 0px))",
        pt: 0,
      }}
    >
      {/* 1. TOP: Featured Carousel - Full Width Edge-to-Edge with 0 padding */}
      <Box
        id="mobile-carousel-container"
        sx={{ width: "100%", position: "relative", mb: 1.5 }}
      >
        <FeaturedCarousel isMobileFullWidth />
      </Box>

      <Container
        maxWidth="sm"
        disableGutters
        sx={{
          px: { xs: 2, sm: 2.5 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* 2. Overview / Upsell Tab Switcher */}
        <MobileDashboardTabs activeTab={activeTab} onChangeTab={setActiveTab} />

        {/* 3. Tab Content with Touch Swipe between Overview and Upsell */}
        <Box
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          sx={{
            width: "100%",
            transition: "opacity 0.2s ease",
            minHeight: "40vh",
          }}
        >
          {activeTab === 0 && <MobileOverviewContent />}
          {activeTab === 1 && <MobileUpsellContent />}
        </Box>
      </Container>
    </Box>
  );
};
